import { push } from "connected-react-router";
import * as api from "api";
import appActionTypes from "containers/App/_actions";
import { fetchFarm } from "containers/Farm/_actions";
import ReactGA from "react-ga4";
import { clearBaseAnalysis } from "containers/BudgetHome/Overview/_actions";
import * as utils from "common/utils";

const actionTypes = {
    BUDGET_TOUCHED: "BUDGET_TOUCHED",
    BUDGET_SYNCING_BEGIN: "BUDGET_SYNCING_BEGIN",
    BUDGET_SYNCING_END: "BUDGET_SYNCING_END",
    BUDGET_FETCHING_BEGIN: "BUDGET_FETCHING_BEGIN",
    BUDGET_FETCHING_END: "BUDGET_FETCHING_END",
    BUDGET_FETCHED: "BUDGET_FETCHED",
    BUDGET_DOWNLOAD: "BUDGET_DOWNLOAD",
    BUDGET_DOWNLOAD_SUCCESS: "BUDGET_DOWNLOAD_SUCCESS",
    BUDGET_SAVED: "BUDGET_SAVED",
    BUDGET_UNDO_CHANGES: "BUDGET_UNDO_CHANGES",
    BUDGET_SAVE_ERROR: "BUDGET_SAVE_ERROR",
    BUDGET_XML_UPLOAD: "BUDGET_XML_UPLOAD",
    CANCEL_GET_ANALYSIS_DIRECTLY: "CANCEL_GET_ANALYSIS_DIRECTLY",
};
export default actionTypes;

// Fetch Budget
export const fetchBudget = (farmId, budgetId, silentMode = false, postActions = []) => {
    const cancelActionType = `${actionTypes.BUDGET_FETCHED}/${budgetId}`;
    return (dispatch, getState) => {
        const { offlineFarms, auth } = getState();
        if (!offlineFarms.some((f) => f.id === farmId)) {
            return dispatch(fetchFarm(farmId, silentMode, budgetId));
        }

        const offlineFarm = offlineFarms.find((f) => f.id === farmId);
        if (offlineFarm.accessDenied) return dispatch(push(`/app/farm/${farmId}/analysis/${budgetId}`));

        const offlineBudget = offlineFarm.farm.offlineBudgets.find((b) => b.id === budgetId);

        dispatch({ type: cancelActionType });
        dispatch(
            api.get({
                path: `farms/${farmId}/budgets/${budgetId}`,
                onBeforeSend: () => {
                    return (dispatch) => {
                        if (offlineBudget) dispatch({ type: actionTypes.BUDGET_FETCHING_BEGIN, payload: offlineBudget.budget });
                    };
                },
                onSuccess: (response) => {
                    return (dispatch) => {
                        dispatch({ type: actionTypes.BUDGET_FETCHED, payload: { budget: response, userId: auth.userId } });
                        postActions.forEach((a) => dispatch(a));
                    };
                },
                onFinally: () => {
                    if (offlineBudget) return { type: actionTypes.BUDGET_FETCHING_END, payload: offlineBudget.budget };
                    return [];
                },
                onFailure: (error) => {
                    error.handled = true;
                    return (dispatch) => {
                        switch (error.status) {
                            case 403:
                                dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You are not authorised to view this analysis." });
                                dispatch(push(`/`));
                                break;
                            default:
                                dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                                break;
                        }
                    };
                },
                silentMode,
                cancelActionType,
            })
        );
    };
};

export function downloadBudgetSuccess() {
    return { type: actionTypes.BUDGET_DOWNLOAD_SUCCESS };
}

export const downloadBudget = (farmId, budgetId) => {
    return (dispatch, getState) => {
        const { auth } = getState();
        return dispatch(
            api.get({
                path: `farms/${farmId}/budgets/${budgetId}/download`,
                onSuccess: (response) => {
                    return (dispatch) => {
                        dispatch({ type: actionTypes.BUDGET_FETCHED, payload: { budget: response, userId: auth.userId } });
                        dispatch({ type: appActionTypes.APP_DOWNLOAD, payload: { content: response, name: response.budget ? response.budget.name : response.name } });
                    };
                },
            })
        );
    };
};

// Create Budget
export const createBudget = (budget, silentMode = false, callSync = false, returnToBudgetHome = false, postActions = []) => {
    ReactGA.event({
        category: "Budget",
        action: "Create",
    });
    const cancelActionType = `${actionTypes.BUDGET_TOUCHED}/${budget.id}`;

    return (dispatch) => {
        dispatch({ type: cancelActionType });
        dispatch(
            api.post({
                path: `farms/${budget.farmId}/budgets`,
                content: budget,
                onBeforeSend: () => {
                    return [
                        { type: actionTypes.BUDGET_TOUCHED, payload: { budget, isNew: true } },
                        { type: actionTypes.BUDGET_SYNCING_BEGIN, payload: budget },
                    ];
                },
                onSuccess: (response) => {
                    return (dispatch) => {
                        dispatch({ type: actionTypes.BUDGET_SAVED, payload: budget });
                        dispatch(fetchBudget(budget.farmId, budget.id, true));
                        if (postActions) postActions.forEach((a) => dispatch(a));
                        if (returnToBudgetHome) dispatch(push(`/app/farm/${budget.farmId}/analysis/${budget.id}`));
                    };
                },
                onFailure: (error) => {
                    if (!error.offline) {
                        return (dispatch) => {
                            dispatch({ type: actionTypes.BUDGET_SAVE_ERROR, payload: { budget, error } });
                        };
                    } else {
                        return [];
                    }
                },
                onFinally: (online) => {
                    return (dispatch) => {
                        dispatch({ type: actionTypes.BUDGET_SYNCING_END, payload: budget });
                        if (callSync) dispatch(syncDirtyBudgets(budget.id));
                        if (!online && returnToBudgetHome) dispatch(push(`/app/farm/${budget.farmId}/analysis/${budget.id}`));
                    };
                },
                cancelActionType,
                silentMode,
            })
        );
    };
};

// Update Budget
export const updateBudget =
    (budget, silentMode = false, callSync = false, returnToBudgetHome = false, isSavingComments = false) =>
    (dispatch, getState) => {
        const cancelActionType = `${actionTypes.BUDGET_TOUCHED}/${budget.id}`;
        return new Promise((resolve, reject) => {
            const { offlineFarms, router } = getState();
            const offlineFarm = offlineFarms.find((f) => f.id === budget.farmId);
            const offlineBudget = offlineFarm.farm.offlineBudgets.find((b) => b.id === budget.id);
            budget.versionId = offlineBudget.budget.versionId;
            utils.cleanBudgetReferences(budget);

            // Do not save again if a save is currently in progress.
            if (offlineBudget.isSaving) {
                return dispatch({ type: actionTypes.BUDGET_TOUCHED, payload: { budget, isSaving: true, isPendingSave: true } });
            }

            // Strip unnecessary properties from request content.
            const requestContent = {
                ...budget,
                currentResults: null,
                created: null,
                lastModified: null,
                messages: null,
                path: router.location.pathname,
                blocks: budget.blocks.map((block) => {
                    return { ...block, currentResults: null };
                }),
            };

            dispatch({ type: cancelActionType });

            dispatch(
                api.put({
                    path: `farms/${budget.farmId}/budgets/${budget.id}`,
                    content: requestContent,
                    timeout: 120000,
                    onBeforeSend: () => {
                        return (dispatch) => {
                            dispatch({ type: actionTypes.BUDGET_TOUCHED, payload: { budget, isSaving: true } });
                            dispatch({ type: actionTypes.BUDGET_SYNCING_BEGIN, payload: budget });
                        };
                    },
                    onSuccess: () => {
                        resolve(budget);
                        const theOfflineFarm = getState().offlineFarms.find((f) => f.id === budget.farmId);
                        const theOfflineBudget = theOfflineFarm.farm.offlineBudgets.find((b) => b.id === budget.id);
                        return (dispatch) => {
                            dispatch({ type: actionTypes.BUDGET_SAVED, payload: budget });
                            if (!theOfflineBudget.isPendingSave) dispatch(fetchBudget(budget.farmId, budget.id, true));
                            if (!silentMode || isSavingComments) dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                            if (returnToBudgetHome) dispatch(push(`/app/farm/${budget.farmId}/analysis/${budget.id}`));
                        };
                    },
                    onFailure: (error) => {
                        if (!error.offline) {
                            reject(error);
                            return (dispatch) => {
                                dispatch({ type: actionTypes.BUDGET_SAVE_ERROR, payload: { budget, error } });
                            };
                        } else {
                            resolve(budget);
                            return (dispatch) => {
                                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                            };
                        }
                    },
                    onFinally: (online) => {
                        const theOfflineFarm = getState().offlineFarms.find((f) => f.id === budget.farmId);
                        const theOfflineBudget = theOfflineFarm.farm.offlineBudgets.find((b) => b.id === budget.id);
                        return (dispatch) => {
                            dispatch({ type: actionTypes.BUDGET_SYNCING_END, payload: budget });
                            if (callSync || theOfflineBudget.isPendingSave) dispatch(syncDirtyBudgets(budget.id));
                            if (!online && returnToBudgetHome) dispatch(push(`/app/farm/${budget.farmId}/analysis/${budget.id}`));
                        };
                    },
                    cancelActionType,
                    silentMode,
                })
            );
        });
    };

// Undo Changes
export const undoChanges = (budget) => {
    ReactGA.event({
        category: "Budget",
        action: "Undo",
    });
    return (dispatch, getState) => {
        const { offlineFarms } = getState();
        const offlineFarm = offlineFarms.find((f) => f.id === budget.farmId);
        const offlineBudget = offlineFarm.farm.offlineBudgets.find((b) => b.id === budget.id);

        dispatch({ type: actionTypes.BUDGET_UNDO_CHANGES, payload: budget });
        if (!offlineBudget.isNew) dispatch(fetchBudget(budget.farmId, budget.id, true));
    };
};

// Delete Budget
export const deleteBudget = (budget, silentMode = false, postAction) => {
    ReactGA.event({
        category: "Budget",
        action: "Delete",
    });
    return (dispatch) => {
        dispatch({ type: actionTypes.BUDGET_TOUCHED, payload: { budget, isDeleted: true } });
        dispatch(
            api.del({
                path: `farms/${budget.farmId}/budgets/${budget.id}`,
                onBeforeSend: () => {
                    return [{ type: actionTypes.BUDGET_SYNCING_BEGIN, payload: budget }];
                },
                onSuccess: (response) => {
                    return (dispatch) => {
                        dispatch({ type: actionTypes.BUDGET_SAVED, payload: budget });
                        dispatch(clearBaseAnalysis(budget.id));
                        dispatch(fetchFarm(budget.farmId, true, null));
                        dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                    };
                },
                onFailure: (error) => {
                    error.handled = true;
                    return (dispatch) => {
                        if (error.status === 404) {
                            // Budget has already been deleted.
                            dispatch({ type: actionTypes.BUDGET_SAVED, payload: budget });
                            dispatch(fetchFarm(budget.farmId));
                            dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                        } else if (error.status === 403) {
                            dispatch({ type: actionTypes.BUDGET_TOUCHED, payload: { budget, isDirty: false, isDeleted: false } });
                            dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You do not have permission to delete this analysis." });
                        } else if (error.offline) {
                            dispatch({ type: actionTypes.BUDGET_TOUCHED, payload: { budget, isDirty: false, isDeleted: false } });
                            dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "Unable to delete this analysis while offline. Please check your internet connection and try again." });
                        } else {
                            dispatch({ type: actionTypes.BUDGET_TOUCHED, payload: { budget, isDirty: false, isDeleted: false } });
                            dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        }
                    };
                },
                onFinally: (online) => {
                    if (postAction) {
                        postAction();
                    }
                    return (dispatch) => {
                        dispatch({ type: actionTypes.BUDGET_SYNCING_END, payload: budget });
                    };
                },
                cancelActionType: actionTypes.BUDGET_TOUCHED,
                silentMode,
            })
        );
    };
};

// Upload Budget XML
export const uploadBudgetXml = (budgetType, farmId, xml, name, year) => {
    ReactGA.event({
        category: "Budget",
        action: "Upload",
    });
    return api.post({
        path: `farms/${farmId}/budgetXml/?budgetType=${budgetType}&name=${encodeURIComponent(name)}&yearEnd=${year}`,
        content: xml,
        contentType: "text/plain",
        cancelActionType: appActionTypes.APP_SUBMISSION_CANCEL,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch(fetchFarm(farmId, true));
                dispatch({ type: appActionTypes.APP_SUBMISSION_OUTCOME, payload: { type: actionTypes.BUDGET_XML_UPLOAD } });
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error) => {
            error.handled = true;
            var messages = [];
            if (error.xhr && error.xhr.response) {
                if (error.xhr.response.length) {
                    for (var i = 0; i < error.xhr.response.length; i++) {
                        messages.push(error.xhr.response[i].message ? error.xhr.response[i].message.text : error.xhr.response[i].errorMessage);
                    }
                } else {
                    messages.push(error.xhr.response.error ? error.xhr.response.error + ". Please try again and if problem persists call support." : "An unexpected error has occured. Please try again or call support if the problem persists.");
                }
            } else {
                messages.push("An unexpected error has occured. Please try again or call support if the problem persists.");
            }
            return { type: appActionTypes.APP_SUBMISSION_OUTCOME, payload: { type: actionTypes.BUDGET_XML_UPLOAD, errors: messages } };
        },
    });
};

// Cancel Upload Budget XML
export const cancelUploadBudgetXml = () => {
    return { type: appActionTypes.APP_SUBMISSION_CANCEL };
};

// Upload Budget JSON
export const uploadBudgetJson = (budgetType, farmId, json, name, year) => {
    ReactGA.event({
        category: "Budget",
        action: "Upload",
    });
    return api.post({
        path: `farms/${farmId}/analysis/loadjson?budgetType=${budgetType}&name=${encodeURIComponent(name)}&yearEnd=${year}`,
        content: json,
        cancelActionType: appActionTypes.APP_SUBMISSION_CANCEL,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch(fetchFarm(farmId, true));
                dispatch({ type: appActionTypes.APP_SUBMISSION_OUTCOME, payload: { type: actionTypes.BUDGET_XML_UPLOAD } });
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error) => {
            error.handled = true;
            var messages = [];
            if (error.response && error.response.error) {
                messages = [error.response.error + " (" + error.response.requestId + ")"];
            } else if (error.response && error.response.errors) {
                messages = error.response.errors.map((e) => `${e.field} (${e.code}): ${e.message}`);
            } else if (error.xhr && error.response) {
                messages = Object.keys(error.response).map((k) => k + ": " + error.response[k].map((t) => t).join(", "));
            } else {
                messages.push("An unexpected error has occured. Please try again or call support if the problem persists.");
            }
            return { type: appActionTypes.APP_SUBMISSION_OUTCOME, payload: { type: actionTypes.BUDGET_XML_UPLOAD, errors: messages } };
        },
    });
};

// Cancel Upload Budget XML
export const cancelUploadBudgetJson = () => {
    return { type: appActionTypes.APP_SUBMISSION_CANCEL };
};

// Grant Budget Access
export const grantBudgetAccess = (budget, options) => {
    ReactGA.event({
        category: "Budget",
        action: "Grant Access",
    });

    return api.put({
        path: `farms/${budget.farmId}/budgets/${budget.id}/access`,
        content: options,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch(fetchBudget(budget.farmId, budget.id, true));
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
            };
        },
    });
};

export const addBudgetToStudentGroup = (budget, options) => {
    ReactGA.event({
        category: "Budget",
        action: "Add budget to student group",
    });

    return api.post({
        path: `farms/${budget.farmId}/budgets/${budget.id}/addtostudentgroup`,
        content: options,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
            };
        },
    });
};

// Publish Budget
export const publishBudget = (publication) => {
    const { farmId, budgetId } = publication;
    ReactGA.event({
        category: "Budget",
        action: "Publish",
    });
    return api.put({
        path: `farms/${farmId}/budgets/${budgetId}/publications`,
        content: publication,
        onBeforeSend: () => {
            return { type: appActionTypes.APP_MODAL_CLEAR_ERROR };
        },
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch(fetchFarm(farmId, true));
                dispatch(fetchBudget(farmId, budgetId));
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                if (error.status === 426) {
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "Publication modified by another user. Please refresh your browser and try again." });
                } else {
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                }
            };
        },
    });
};

// Sync Dirty Budgets
export const syncDirtyBudgets = (budgetId) => {
    return (dispatch, getState) => {
        const { offlineFarms = [] } = getState();
        offlineFarms.forEach((offlineFarm) => {
            const { farm = {} } = offlineFarm;
            const { offlineBudgets = [] } = farm;
            offlineBudgets.forEach((offlineBudget) => {
                if (offlineBudget.isDirty || offlineBudget.isPendingSave) {
                    if (offlineBudget.isNew) dispatch(createBudget(offlineBudget.budget, budgetId !== offlineBudget.id));
                    else dispatch(updateBudget(offlineBudget.budget, offlineBudget.isPendingSave ? true : budgetId !== offlineBudget.id));
                }
            });
        });
    };
};

export const getAnalysisDirectly = (farmId, budgetId) => (dispatch) => {
    return new Promise((resolve, reject) => {
        ReactGA.event({
            category: "Analysis",
            action: "getAnalysisDirectly",
        });
        dispatch({ type: actionTypes.CANCEL_GET_ANALYSIS_DIRECTLY });
        dispatch(
            api.get({
                path: `farms/${farmId}/budgets/${budgetId}`,
                onSuccess: (response) => {
                    resolve(response);
                    return [];
                },
                onFailure: (error) => {
                    error.handled = true;
                    reject(error);
                    return [];
                },
                cancelActionType: actionTypes.CANCEL_GET_ANALYSIS_DIRECTLY,
            })
        );
    });
};
